/**
 * ngxh6 /debug pages: the access log (who called which endpoint) and the
 * Messenger queue. Access entries are generated per Darwin hour, like the app
 * logs, using the persona accounts and documentation-range IP addresses.
 */
import { currentQuarter, iso, now } from '../../clock';
import { PERSONAS } from '../../personas';
import { rng } from '../../rng';
import { fakeUlid } from '../../world';
import { darwinHour, darwinHourKey, startOfDarwinHour } from './time';

export interface AccessEntry {
    id: string;
    timestamp: string;
    username: string;
    method: string;
    requestUri: string;
    originUri: string;
    ip: string;
}

function visits(q: string): Record<string, [string, string, string][]> {
    // persona key -> [method, requestUri, originUri]
    return {
        ceo: [
            ['GET', `/api/dashboard?quarter=${q}`, '/'],
            ['GET', `/api/finance/stats?quarter=${q}`, '/finance'],
            ['GET', '/api/budget/years', '/finance/budgets'],
            ['GET', '/api/v1/users?itemsPerPage=10&page=1', '/users'],
            ['GET', `/api/quarterly-report/${q}`, '/reports/quarterly-ceo'],
            ['GET', '/api/myob/status', '/settings/integrations'],
        ],
        coo: [
            ['GET', `/api/dashboard?quarter=${q}`, '/'],
            ['GET', `/api/incidents/stats?quarter=${q}`, '/clients/incidents'],
            ['GET', `/api/eh-data/stats?quarter=${q}`, '/people'],
            ['GET', `/api/finance/stats?quarter=${q}`, '/finance'],
            ['POST', '/api/chat/message', '/'],
        ],
        care: [
            ['GET', `/api/incidents/stats?quarter=${q}`, '/clients/incidents'],
            ['GET', `/api/medications/stats?quarter=${q}`, '/clients/medications'],
            ['GET', `/api/goals/stats?quarter=${q}`, '/clients/goals'],
            ['GET', '/api/v1/sil_referrals?itemsPerPage=10&page=1', '/clients/referrals'],
            ['POST', '/api/incidents/sync', '/clients/incidents'],
        ],
        hr: [
            ['GET', `/api/eh-data/stats?quarter=${q}`, '/people/employment-hero'],
            ['GET', '/api/training/stats', '/people/training'],
            ['GET', '/api/trends/employment_hero?days=365', '/people'],
            ['GET', '/api/me/compliance', '/user-admin/compliance'],
        ],
        board: [
            ['GET', '/api/board/dashboard', '/board'],
            ['GET', `/api/board/finance/stats?quarter=${q}`, '/finance'],
            ['GET', `/api/board/incidents/stats?quarter=${q}`, '/clients/incidents'],
        ],
    };
}

const IPS: Record<string, string> = { ceo: '203.0.113.24', coo: '203.0.113.31', care: '198.51.100.17', hr: '203.0.113.58', board: '198.51.100.92' };

/** Access entries over the last 48 hours, newest first. */
export function accessEntries(): AccessEntry[] {
    const q = currentQuarter().key;
    const byPersona = visits(q);
    const top = startOfDarwinHour();
    const nowMs = now().getTime();
    const out: (AccessEntry & { t: number })[] = [];
    for (let h = 0; h < 48; h++) {
        const start = new Date(top.getTime() - h * 3600000);
        const hour = darwinHour(start);
        if (hour < 7 || hour > 19) {
            continue;
        }
        const key = darwinHourKey(start);
        const r = rng(`access:${key}`);
        const n = r.int(0, 3);
        for (let i = 0; i < n; i++) {
            const p = r.weighted([[PERSONAS[0], 4], [PERSONAS[1], 4], [PERSONAS[2], 3], [PERSONAS[3], 2], [PERSONAS[4], 1]] as [(typeof PERSONAS)[number], number][]);
            const at = start.getTime() + r.int(0, 3599) * 1000;
            const login = r.bool(0.2);
            const [method, requestUri, originUri] = login ? ['POST', '/api/user/login_check', '/login'] : r.pick(byPersona[p.key]);
            out.push({ id: fakeUlid(`access:${key}:${i}`), timestamp: iso(new Date(at)), username: p.email, method, requestUri, originUri, ip: IPS[p.key], t: at });
        }
    }
    return out
        .filter(e => e.t <= nowMs)
        .sort((a, b) => b.t - a.t)
        .map(({ t, ...e }) => e);
}

export function accessEntryDetail(id: string): Record<string, unknown> | null {
    const e = accessEntries().find(x => x.id === id);
    if (!e) {
        return null;
    }
    return { ...e, headers: { 'user-agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/18.0 Safari/605.1.15', 'x-referrer': e.originUri, accept: 'application/json' } };
}

/** The Messenger queue: healthy and empty, as it is between syncs. */
export function messengerMessages(): Record<string, unknown>[] {
    return [];
}
